import { useState } from "react";
import { Select } from "../input-functions";
import Button from "../button";
import Modal from "../modal";
import {
  IdLookupResponse,
  IdLookupType,
  useLazyGetIdLookupQuery,
} from "../../actions/id-lookup.action";

export interface IdLookupSelection {
  userId: string;
  workerId: string;
  employerId: string;
}

interface IdLookupProps {
  onSelect?: (selection: IdLookupSelection) => void;
  buttonLabel?: string;
}

const typeOptions = [
  { value: "userId", label: "User ID (LUxx)" },
  { value: "clientUniqueRefId", label: "Client User ID (USRxx)" },
];

export default function IdLookup({
  onSelect,
  buttonLabel = "ID Lookup",
}: IdLookupProps) {
  const [open, setOpen] = useState(false);
  const [type, setType] = useState<IdLookupType>("clientUniqueRefId");
  const [id, setId] = useState("");
  const [error, setError] = useState("");
  const [result, setResult] = useState<IdLookupResponse | null>(null);
  const [triggerLookup, { isFetching }] = useLazyGetIdLookupQuery();

  async function handleLookup() {
    const trimmed = id.trim();
    if (!trimmed) {
      setError("Enter an ID to look up");
      return;
    }
    setError("");
    setResult(null);
    try {
      const response = await triggerLookup({ type, id: trimmed }).unwrap();
      setResult(response);
    } catch {
      setError("Lookup failed");
    }
  }

  function handleClose() {
    setOpen(false);
    setError("");
  }

  function handleSelect(selection: IdLookupSelection) {
    onSelect?.(selection);
    handleClose();
  }

  return (
    <>
      <Button variant="light-blue" onClick={() => setOpen(true)}>
        {buttonLabel}
      </Button>
      <Modal
        open={open}
        onClose={handleClose}
        title="ID Lookup"
        size="large"
        hasSubmitButton={false}
        hasCancelButton={false}
      >
        <div className="row g-2 align-items-end">
          <div className="col-12 col-md-4">
            <label className="form-label">Lookup By</label>
            <Select
              value={type}
              onChange={(e) => setType(e.target.value as IdLookupType)}
              options={typeOptions}
            />
          </div>
          <div className="col-12 col-md-5">
            <label className="form-label">Value</label>
            <input
              type="text"
              className="form-control"
              value={id}
              onChange={(e) => setId(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleLookup()}
            />
          </div>
          <div className="col-12 col-md-2">
            <Button
              variant="primary"
              onClick={handleLookup}
              disabled={isFetching}
            >
              {isFetching ? "Searching..." : "Lookup"}
            </Button>
          </div>
        </div>
        {error && <div className="alert alert-danger mt-3 mb-0">{error}</div>}
        {result && (
          <div className="mt-3">
            <div className="mb-2">
              <strong>User</strong>
              <div className="small">
                {result.user.name && <div>Name: {result.user.name}</div>}
                <div>User ID: {result.user._id}</div>
                <div>Client User ID: {result.user.clientUniqueRefId}</div>
              </div>
            </div>
            <strong>Workers</strong>
            {result.relations.length === 0 ? (
              <div className="small text-muted">No workers linked</div>
            ) : (
              <div className="table-responsive">
                <table className="table table-sm small mt-1 mb-0">
                  <thead>
                    <tr>
                      <th>Worker ID</th>
                      <th>Worker Ref</th>
                      <th>User ID</th>
                      <th>Employer ID</th>
                      <th>Employer</th>
                      {onSelect && <th></th>}
                    </tr>
                  </thead>
                  <tbody>
                    {result.relations.map((relation, index) => (
                      <tr key={index}>
                        <td>{relation.worker._id}</td>
                        <td>{relation.worker.clientRefId}</td>
                        <td>{relation.worker.userId}</td>
                        <td>{relation.worker.employerId}</td>
                        <td>
                          {relation.employer
                            ? `${relation.employer.name} (${relation.employer.clientRefId})`
                            : "Employer not found"}
                        </td>
                        {onSelect && (
                          <td>
                            {relation.employer &&
                              (() => {
                                const employer = relation.employer;
                                return (
                                  <Button
                                    variant="blue-text"
                                    size="xs"
                                    onClick={() =>
                                      handleSelect({
                                        userId: result.user._id,
                                        workerId: relation.worker._id,
                                        employerId: employer._id,
                                      })
                                    }
                                  >
                                    Select
                                  </Button>
                                );
                              })()}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </Modal>
    </>
  );
}